import type { SocialAsset, SocialDraft, SocialDraftsResponse, SocialRun } from '@nono/contract';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { ApiError, api, queryClient } from '@/lib/api';
import { type Locale, useLocale, useViewMessages } from '@/lib/i18n';
import { cn } from '@/lib/utils';

/*
 * Innhold — the approval queue for the social-media content agents.
 *
 * The agents (editor → specialist per topic → reviewer) draft next week's
 * posts; a human approves, edits or rejects each one here. Nothing posts from
 * this page. An edit is saved next to the agents' original, and a rejection
 * needs a reason — both are what the editor reads before planning again.
 */

type Tab = 'pending' | 'approved' | 'rejected';

interface InnholdMessages {
  title: string;
  sub: string;
  tabs: Record<Tab, string>;
  pillar: Record<string, string>;
  format: Record<string, string>;
  agentsOff: string;
  libraryEmpty: string;
  library: (usable: number, total: number) => string;
  lastRun: (week: string, status: string) => string;
  runStatus: Record<string, string>;
  cost: (usd: string, calls: number) => string;
  runButton: string;
  running: string;
  runError: string;
  empty: Record<Tab, string>;
  reviewPassed: string;
  reviewFlagged: string;
  attempts: (n: number) => string;
  brief: string;
  why: string;
  files: string;
  missingFile: string;
  needsLabel: (label: string) => string;
  healthInFile: string;
  missingVisual: string;
  approve: string;
  approveEdited: string;
  reject: string;
  rejectReason: string;
  confirmReject: string;
  cancel: string;
  reopen: string;
  editedNote: string;
  decidedBy: (who: string, when: string) => string;
  reasonLabel: string;
  saveError: string;
  issueSeverity: Record<'block' | 'fix' | 'note', string>;
}

const MESSAGES: Record<Locale, InnholdMessages> = {
  nb: {
    title: 'Innhold · sosiale medier',
    sub: 'Agentene lager utkast til neste ukes innlegg. Du godkjenner, retter eller avviser hvert ett – ingenting postes herfra. Rettelser og grunner lagres, og redaktøren leser dem før neste plan.',
    tabs: { pending: 'Venter', approved: 'Godkjent', rejected: 'Avvist' },
    pillar: {
      medlemshistorier: 'Medlemshistorier',
      vertskapet: 'Vertskapet',
      'styrke-forklart': 'Styrke forklart',
      stedet: 'Stedet og fellesskapet',
      provetime: 'Prøvetime',
    },
    format: { reel: 'Reel', carousel: 'Karusell', image: 'Bilde', story: 'Story' },
    agentsOff:
      'Agentene er av. Legg inn ANTHROPIC_API_KEY under Railway → Variables, så kan du lage en ukeplan her (og den lages av seg selv hver fredag kl. 07).',
    libraryEmpty:
      'Bildebiblioteket er ikke lastet opp ennå, så agentene har ingen filer å velge fra.',
    library: (u, t) => `${u} av ${t} filer i biblioteket har avklart samtykke`,
    lastRun: (w, s) => `Siste runde: uken fra ${w} · ${s}`,
    runStatus: { running: 'jobber …', ok: 'ferdig', error: 'feilet' },
    cost: (usd, calls) => `≈ ${usd} USD · ${calls} kall`,
    runButton: 'Lag ukeplan',
    running: 'Agentene jobber …',
    runError: 'Kunne ikke starte',
    empty: {
      pending: 'Ingen utkast venter. Lag en ukeplan for å fylle køen.',
      approved: 'Ingen godkjente utkast ennå.',
      rejected: 'Ingen avviste utkast.',
    },
    reviewPassed: 'Besto kvalitetssjekken',
    reviewFlagged: 'Merket av kvalitetssjekken',
    attempts: (n) => (n > 1 ? `skrevet om ${n - 1} gang` : 'første forsøk'),
    brief: 'Oppdraget',
    why: 'Begrunnelse',
    files: 'Filer fra biblioteket',
    missingFile: 'Mangler fil',
    needsLabel: (l) => `krever «${l}»`,
    healthInFile: 'helse nevnt i filen',
    missingVisual: 'Må lages eller filmes',
    approve: 'Godkjenn',
    approveEdited: 'Godkjenn med rettelse',
    reject: 'Avvis',
    rejectReason: 'Hvorfor? Redaktøren leser dette (minst 8 tegn)',
    confirmReject: 'Avvis utkastet',
    cancel: 'Avbryt',
    reopen: 'Angre',
    editedNote: 'Rettet før godkjenning. Agentenes original:',
    decidedBy: (w, t) => `${w} · ${t}`,
    reasonLabel: 'Grunn',
    saveError: 'Kunne ikke lagre, prøv igjen.',
    issueSeverity: { block: 'Stopper', fix: 'Bør rettes', note: 'Forslag' },
  },
  en: {
    title: 'Content · social media',
    sub: 'The agents draft next week’s posts. You approve, edit or reject each one — nothing is posted from here. Edits and reasons are saved, and the editor reads them before the next plan.',
    tabs: { pending: 'Waiting', approved: 'Approved', rejected: 'Rejected' },
    pillar: {
      medlemshistorier: 'Member stories',
      vertskapet: 'The hosts',
      'styrke-forklart': 'Strength explained',
      stedet: 'The place and community',
      provetime: 'Trial',
    },
    format: { reel: 'Reel', carousel: 'Carousel', image: 'Image', story: 'Story' },
    agentsOff:
      'The agents are off. Add ANTHROPIC_API_KEY under Railway → Variables to make a weekly plan here (it also runs by itself every Friday at 07:00).',
    libraryEmpty:
      'The photo library has not been uploaded yet, so the agents have no files to choose from.',
    library: (u, t) => `${u} of ${t} library files have cleared consent`,
    lastRun: (w, s) => `Last run: week of ${w} · ${s}`,
    runStatus: { running: 'working…', ok: 'done', error: 'failed' },
    cost: (usd, calls) => `≈ ${usd} USD · ${calls} calls`,
    runButton: 'Make weekly plan',
    running: 'The agents are working…',
    runError: 'Could not start',
    empty: {
      pending: 'No drafts waiting. Make a weekly plan to fill the queue.',
      approved: 'No approved drafts yet.',
      rejected: 'No rejected drafts.',
    },
    reviewPassed: 'Passed the quality check',
    reviewFlagged: 'Flagged by the quality check',
    attempts: (n) => (n > 1 ? `rewritten ${n - 1} time` : 'first attempt'),
    brief: 'The brief',
    why: 'Rationale',
    files: 'Files from the library',
    missingFile: 'File missing',
    needsLabel: (l) => `needs «${l}»`,
    healthInFile: 'health mentioned in file',
    missingVisual: 'Needs to be made or filmed',
    approve: 'Approve',
    approveEdited: 'Approve with edit',
    reject: 'Reject',
    rejectReason: 'Why? The editor reads this (at least 8 characters)',
    confirmReject: 'Reject the draft',
    cancel: 'Cancel',
    reopen: 'Undo',
    editedNote: 'Edited before approval. The agents’ original:',
    decidedBy: (w, t) => `${w} · ${t}`,
    reasonLabel: 'Reason',
    saveError: 'Could not save, try again.',
    issueSeverity: { block: 'Blocks', fix: 'Should fix', note: 'Suggestion' },
  },
};

/** Opus 5.5 list prices per million tokens; a rough guide, not an invoice. */
function estimateUsd(u: Record<string, number> | null): string | null {
  if (!u?.calls) return null;
  const usd =
    ((u.input ?? 0) * 4 +
      (u.output ?? 0) * 20 +
      (u.cacheRead ?? 0) * 0.2 +
      (u.cacheWrite ?? 0) * 5) /
    1_000_000;
  return usd.toFixed(2);
}

function weekday(iso: string | null, locale: Locale): string {
  if (!iso) return '';
  return new Date(`${iso}T12:00:00Z`).toLocaleDateString(locale === 'nb' ? 'nb-NO' : 'en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}

const KEY = (tab: Tab) => ['social-drafts', tab] as const;

export function InnholdPage() {
  const m = useViewMessages(MESSAGES);
  const [tab, setTab] = useState<Tab>('pending');
  const q = useQuery({
    queryKey: KEY(tab),
    queryFn: () => api.socialDrafts(tab),
    staleTime: 0,
    refetchInterval: (query) => (query.state.data?.lastRun?.status === 'running' ? 10_000 : false),
  });
  const run = useMutation({
    mutationFn: () => api.socialRun(),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['social-drafts'] }),
  });

  if (q.isLoading) return <LoadingState />;
  if (q.isError) return <ErrorState onRetry={() => void q.refetch()} />;
  const d = q.data as SocialDraftsResponse;
  const isRunning = d.lastRun?.status === 'running';

  return (
    <div className="flex flex-col gap-5 p-4 md:p-6">
      <div>
        <h1 className="text-2xl font-bold">{m.title}</h1>
        <p className="max-w-3xl text-sm text-fg-subtle">{m.sub}</p>
      </div>

      {!d.agentsConfigured && (
        <Card className="border-warn-fg/40 p-3 text-sm text-warn-fg">{m.agentsOff}</Card>
      )}
      {d.library.total === 0 && (
        <Card className="border-warn-fg/40 p-3 text-sm text-warn-fg">{m.libraryEmpty}</Card>
      )}

      <Card className="flex flex-wrap items-center justify-between gap-3 p-3">
        <div className="flex flex-col gap-0.5 text-sm">
          {d.library.total > 0 && <span>{m.library(d.library.usable, d.library.total)}</span>}
          {d.lastRun && <RunLine run={d.lastRun} />}
        </div>
        <div className="flex items-center gap-2">
          {run.isError && (
            <span className="text-xs text-bad-fg">
              {m.runError}
              {run.error instanceof ApiError ? `: ${run.error.message}` : ''}
            </span>
          )}
          <button
            type="button"
            disabled={!d.agentsConfigured || isRunning || run.isPending}
            onClick={() => run.mutate()}
            className="rounded-md bg-brand px-3 py-2 text-sm font-semibold text-white disabled:opacity-40"
          >
            {isRunning || run.isPending ? m.running : m.runButton}
          </button>
        </div>
      </Card>

      <div className="flex gap-1 border-b border-border" role="tablist">
        {(['pending', 'approved', 'rejected'] as Tab[]).map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            onClick={() => setTab(t)}
            className={cn(
              '-mb-px border-b-2 px-3 py-2 text-sm font-semibold',
              tab === t ? 'border-brand text-fg' : 'border-transparent text-fg-subtle',
            )}
          >
            {m.tabs[t]}
            {d.counts[t] ? <span className="ml-1.5 text-fg-subtle">{d.counts[t]}</span> : null}
          </button>
        ))}
      </div>

      {d.drafts.length === 0 ? (
        <p className="py-8 text-center text-sm text-fg-subtle">{m.empty[tab]}</p>
      ) : (
        <div className="flex flex-col gap-4">
          {d.drafts.map((draft) => (
            <DraftCard key={draft.id} draft={draft} assets={d.assets} />
          ))}
        </div>
      )}
    </div>
  );
}

function RunLine({ run }: { run: SocialRun }) {
  const m = useViewMessages(MESSAGES);
  const usd = estimateUsd(run.usage);
  return (
    <span className="text-fg-subtle">
      {m.lastRun(run.weekOf ?? '?', m.runStatus[run.status] ?? run.status)}
      {usd && run.usage ? ` · ${m.cost(usd, run.usage.calls ?? 0)}` : ''}
      {run.error ? ` · ${run.error}` : ''}
    </span>
  );
}

function DraftCard({ draft, assets }: { draft: SocialDraft; assets: Record<string, SocialAsset> }) {
  const m = useViewMessages(MESSAGES);
  const { locale } = useLocale();
  const [caption, setCaption] = useState(draft.editedCaption ?? draft.caption);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const decide = useMutation({
    mutationFn: (body: Parameters<typeof api.socialDecide>[1]) => api.socialDecide(draft.id, body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['social-drafts'] }),
  });
  const pending = draft.status === 'pending';
  const edited = caption.trim() !== draft.caption.trim();
  const review = draft.review;

  return (
    <Card className="flex flex-col gap-3 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold capitalize">
          {weekday(draft.plannedFor, locale)}
        </span>
        <Badge tone="brand">{m.pillar[draft.pillar] ?? draft.pillar}</Badge>
        <Badge>{m.format[draft.format] ?? draft.format}</Badge>
        <Badge>{draft.platforms.join(' + ')}</Badge>
        {review && (
          <Badge tone={draft.reviewPassed ? 'good' : 'warn'}>
            {draft.reviewPassed ? m.reviewPassed : m.reviewFlagged} · {m.attempts(draft.attempts)}
          </Badge>
        )}
      </div>

      {review && (review.summary || review.issues.length > 0) && (
        <div className="rounded-md bg-surface-2 p-3 text-sm">
          <p>{review.summary}</p>
          {review.issues.length > 0 && (
            <ul className="mt-2 flex flex-col gap-1">
              {review.issues.map((i, n) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: a stored review never reorders; issues have no id
                <li key={n} className="text-xs">
                  <span
                    className={cn(
                      'mr-1.5 font-semibold',
                      i.severity === 'block'
                        ? 'text-bad-fg'
                        : i.severity === 'fix'
                          ? 'text-warn-fg'
                          : 'text-fg-subtle',
                    )}
                  >
                    {m.issueSeverity[i.severity]}
                  </span>
                  {i.problem} → {i.fix}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="flex flex-col gap-2">
          {pending ? (
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              rows={Math.min(14, Math.max(6, caption.split('\n').length + 1))}
              className="w-full rounded-lg border border-border bg-bg p-3 text-sm leading-relaxed"
              aria-label="Bildetekst"
            />
          ) : (
            <p className="whitespace-pre-wrap rounded-lg border border-border bg-bg p-3 text-sm leading-relaxed">
              {draft.editedCaption ?? draft.caption}
            </p>
          )}
          {draft.hashtags?.length ? (
            <p className="text-xs text-fg-subtle">{draft.hashtags.map((h) => `#${h}`).join(' ')}</p>
          ) : null}
          {draft.editedCaption && !pending && (
            <details className="text-xs text-fg-subtle">
              <summary>{m.editedNote}</summary>
              <p className="mt-1 whitespace-pre-wrap">{draft.caption}</p>
            </details>
          )}
        </div>

        <div className="flex flex-col gap-2 text-sm">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-fg-subtle">
            {m.files}
          </h3>
          <ol className="flex flex-col gap-1.5">
            {(draft.slides ?? []).map((s, n) => {
              const a = s.assetId ? assets[s.assetId] : undefined;
              return (
                // biome-ignore lint/suspicious/noArrayIndexKey: slide order IS the identity (the same file may appear twice)
                <li key={n} className="rounded-md border border-border px-2 py-1.5 text-xs">
                  <span className="font-mono font-semibold">{s.assetId ?? m.missingFile}</span>
                  {a && (
                    <>
                      <span className="text-fg-subtle"> · {a.type}</span>
                      {a.label && (
                        <span className="ml-1 font-semibold text-warn-fg">
                          {m.needsLabel(a.label)}
                        </span>
                      )}
                      {a.healthMentioned && (
                        <span className="ml-1 text-warn-fg">· {m.healthInFile}</span>
                      )}
                      <p className="mt-0.5 text-fg-subtle">{a.description}</p>
                    </>
                  )}
                  {s.text && <p className="mt-0.5">«{s.text}»</p>}
                </li>
              );
            })}
          </ol>
          {draft.missingVisual && (
            <div className="rounded-md border border-warn-fg/40 p-2 text-xs">
              <span className="font-semibold text-warn-fg">{m.missingVisual}: </span>
              {draft.missingVisual}
            </div>
          )}
          <details className="text-xs text-fg-subtle">
            <summary>
              {m.brief} · {m.why}
            </summary>
            {draft.brief && <p className="mt-1 whitespace-pre-wrap">{draft.brief}</p>}
            {draft.rationale && <p className="mt-1 whitespace-pre-wrap">{draft.rationale}</p>}
          </details>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
        {pending && !rejecting && (
          <>
            <button
              type="button"
              disabled={decide.isPending}
              onClick={() =>
                decide.mutate(
                  edited ? { action: 'approve', caption: caption.trim() } : { action: 'approve' },
                )
              }
              className="rounded-md bg-good-tint px-3 py-1.5 text-sm font-semibold text-good-fg disabled:opacity-40"
            >
              {edited ? m.approveEdited : m.approve}
            </button>
            <button
              type="button"
              onClick={() => setRejecting(true)}
              className="rounded-md border border-border px-3 py-1.5 text-sm font-semibold"
            >
              {m.reject}
            </button>
          </>
        )}
        {pending && rejecting && (
          <>
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={m.rejectReason}
              className="min-w-0 flex-1 rounded-md border border-border bg-bg px-2 py-1.5 text-sm"
            />
            <button
              type="button"
              disabled={reason.trim().length < 8 || decide.isPending}
              onClick={() => decide.mutate({ action: 'reject', reason: reason.trim() })}
              className="rounded-md bg-bad-tint px-3 py-1.5 text-sm font-semibold text-bad-fg disabled:opacity-40"
            >
              {m.confirmReject}
            </button>
            <button
              type="button"
              onClick={() => {
                setRejecting(false);
                setReason('');
              }}
              className="rounded-md border border-border px-3 py-1.5 text-sm"
            >
              {m.cancel}
            </button>
          </>
        )}
        {!pending && (
          <>
            <span className="text-xs text-fg-subtle">
              {m.decidedBy(
                draft.decidedBy ?? '?',
                draft.decidedAt ? new Date(draft.decidedAt).toLocaleString() : '',
              )}
              {draft.decisionReason ? ` · ${m.reasonLabel}: ${draft.decisionReason}` : ''}
            </span>
            <button
              type="button"
              disabled={decide.isPending}
              onClick={() => decide.mutate({ action: 'reopen' })}
              className="ml-auto rounded-md border border-border px-3 py-1.5 text-xs font-semibold"
            >
              {m.reopen}
            </button>
          </>
        )}
        {decide.isError && <span className="text-xs text-bad-fg">{m.saveError}</span>}
      </div>
    </Card>
  );
}
